import Layout from '@/components/feature/Layout';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { PageBreadcrumb, RelatedLinks, ctaSidebar } from '@/pages/shared/PageHelpers';
import { products } from '@/mocks/products';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Extrusora para Soldagem de Geomembrana',
  description:
    'Entenda a soldagem por extrusão, a diferença para a cunha quente, aplicações, características e os produtos reais da SoldaGeo.',
  url: 'https://maquinadesoldageomembrana.com.br/extrusora-geomembrana',
  inLanguage: 'pt-BR',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'O que é uma extrusora para soldagem de geomembrana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'É um equipamento que funde fio de PEAD por rosca extrusora e o aplica sobre a emenda, criando uma solda com alta resistência mecânica para cantos, junções e reparos.',
      },
    },
    {
      '@type': 'Question',
      name: 'Qual a diferença entre extrusão e cunha quente?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A cunha quente funde as duas folhas simultaneamente e é ideal para grandes áreas planas. A extrusão aplica material fundido sobre a emenda e é indicada para cantos, junções, reparos e geomembranas espessas acima de 2 mm.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quando devo usar uma extrusora?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Use a extrusora para cantos, junções em T, reparos de danos, fixação de acessórios e geomembranas espessas acima de 2 mm, onde a cunha quente não é eficiente.',
      },
    },
  ],
};

const faq = [
  {
    question: 'O que é uma extrusora para soldagem de geomembrana?',
    answer:
      'É um equipamento que funde fio de PEAD por rosca extrusora e o aplica sobre a emenda, criando uma solda com alta resistência mecânica para cantos, junções e reparos.',
  },
  {
    question: 'Qual a diferença entre extrusão e cunha quente?',
    answer:
      'A cunha quente funde as duas folhas simultaneamente e é ideal para grandes áreas planas. A extrusão aplica material fundido sobre a emenda e é indicada para cantos, junções, reparos e geomembranas espessas acima de 2 mm.',
  },
  {
    question: 'Quando devo usar uma extrusora?',
    answer:
      'Use a extrusora para cantos, junções em T, reparos de danos, fixação de acessórios e geomembranas espessas acima de 2 mm, onde a cunha quente não é eficiente.',
  },
];

export default function ExtrusoraGeomembranaPage() {
  const extrusoras = products.filter((p) => p.category === 'Extrusora');

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <PageBreadcrumb items={[{ label: 'Extrusora' }]} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
          <div className="lg:col-span-2">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-6">
              Extrusora para Soldagem de Geomembrana
            </h1>

            <div className="space-y-5 text-foreground-600 leading-relaxed">
              <p>
                A <strong className="text-foreground-950">extrusora para soldagem de geomembrana</strong> é o equipamento complementar indispensável em qualquer projeto de impermeabilização. Ela funde fio de PEAD e o aplica sobre a emenda, gerando soldas de alta resistência mecânica em pontos onde a cunha quente não é eficiente.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Soldagem por Extrusão</h2>
              <p>
                No processo de extrusão, um fio de PEAD é alimentado pela rosca extrusora, fundido a temperaturas que podem chegar a <strong>600–700°C</strong>, e aplicado diretamente sobre a junção das folhas previamente preparadas. O material fundido une as duas superfícies, formando uma solda robusta e resistente.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Diferença entre Extrusão e Cunha Quente</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-5">
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-2 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-500" /> Cunha Quente
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">Funde as duas folhas simultaneamente. Ideal para emendas longas em grandes áreas planas.</p>
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 uppercase mb-2 flex items-center gap-2">
                    <i className="ri-drop-line text-accent-500" /> Extrusão
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed">Aplica fio fundido sobre a emenda. Ideal para cantos, junções, reparos e espessuras acima de 2 mm.</p>
                </div>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Aplicações da Extrusora</h2>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Cantos e junções em T</strong> — onde a cunha quente não alcança.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Reparos de danos</strong> — correção de furos, rasgos e emendas defeituosas.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Fixação de acessórios</strong> — tubos, flange e conexões de drenagem.</span></li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" /><span><strong>Geomembranas espessas</strong> — acima de 2 mm, onde a cunha quente perde eficiência.</span></li>
              </ul>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mt-8 mb-3">Extrusoras Disponíveis</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
                {extrusoras.map((p) => (
                  <div key={p.id} className="bg-background-100 rounded-xl border border-background-200/70 p-4">
                    <p className="text-foreground-900 text-sm font-bold">{p.name}</p>
                    <p className="text-foreground-500 text-xs mt-1">{p.specs.potencia} · {p.specs.temperatura}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5">Perguntas Frequentes</h2>
              <FaqAccordion items={faq} />
            </div>

            <RelatedLinks
              links={[
                { to: '/cunha-quente-geomembrana', label: 'Cunha Quente' },
                { to: '/maquina-de-solda-de-geomembrana', label: 'Máquina de Solda de Geomembrana' },
                { to: '/soldagem-de-geomembrana', label: 'Técnicas de Soldagem' },
                { to: '/modelos-de-maquinas', label: 'Comparativo de Modelos' },
              ]}
            />
          </div>

          <div className="space-y-6">{ctaSidebar}</div>
        </div>
      </div>
    </Layout>
  );
}